import React, { useState, useEffect } from 'react';
import {
  ReaderSettings,
  ReaderTheme,
  FontSizeSetting,
  LineSpacingSetting,
  FontFaceSetting,
} from '../../types/reader';
import { Settings, X, Palette, Type, Sliders, AlertTriangle } from 'lucide-react';

export interface SettingsModalProps {
  isOpen: boolean;
  settings: ReaderSettings;
  onClose: () => void;
  onUpdateSettings: (settings: Partial<ReaderSettings>) => void;
  onResetAllData: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  settings,
  onClose,
  onUpdateSettings,
  onResetAllData,
}) => {
  const [confirmReset, setConfirmReset] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const themes: { id: ReaderTheme; label: string; desc: string; color: string }[] = [
    { id: 'void_black', label: 'Void Black', desc: 'Standard deep vacuum space', color: '#00f3ff' },
    { id: 'terminal_cyan', label: 'Terminal Cyan', desc: 'Phosphor console CRT', color: '#00ffcc' },
    { id: 'planck_amber', label: 'Planck Amber', desc: 'Resonant reactor gold', color: '#ffb700' },
    { id: 'core_crimson', label: 'Core Crimson', desc: 'Containment breach alert', color: '#ff2a4b' },
  ];

  const fontSizes: { id: FontSizeSetting; label: string }[] = [
    { id: 'small', label: 'Compact (14px)' },
    { id: 'medium', label: 'Standard (16px)' },
    { id: 'large', label: 'Spacious (18px)' },
    { id: 'xlarge', label: 'Prominent (21px)' },
  ];

  const lineSpacings: { id: LineSpacingSetting; label: string }[] = [
    { id: 'compact', label: 'Dense (1.4)' },
    { id: 'normal', label: 'Comfort (1.7)' },
    { id: 'relaxed', label: 'Spacious (2.0)' },
  ];

  const fontFaces: { id: FontFaceSetting; label: string; preview: string }[] = [
    { id: 'serif', label: 'Literary Serif', preview: 'Spectral / Georgia' },
    { id: 'mono', label: 'Telemetry Mono', preview: 'JetBrains / Monospace' },
    { id: 'sans', label: 'Modern Sans', preview: 'Inter / System' },
  ];

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Reader Settings"
      >
        <div className="modal-header">
          <div className="modal-title-group">
            <Settings size={20} className="modal-icon-accent" />
            <h2 className="modal-title">Reader Configuration</h2>
          </div>
          <button className="btn-close" onClick={onClose} aria-label="Close Settings">
            <X size={20} />
          </button>
        </div>

        <div className="modal-content scrollable">
          {/* Section 1: Themes */}
          <section className="settings-section">
            <div className="section-header">
              <Palette size={16} className="section-icon" />
              <h3 className="section-title">Visual Atmosphere Theme</h3>
            </div>
            <div className="theme-grid">
              {themes.map((t) => {
                const isActive = settings.theme === t.id;
                return (
                  <button
                    key={t.id}
                    className={`theme-card theme-card-${t.id} ${isActive ? 'active' : ''}`}
                    onClick={() => onUpdateSettings({ theme: t.id })}
                  >
                    <div className="theme-swatch" style={{ borderColor: t.color }}>
                      <span className="swatch-indicator" style={{ backgroundColor: t.color }} />
                    </div>
                    <div className="theme-info">
                      <span className="theme-name">{t.label}</span>
                      <span className="theme-desc">{t.desc}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Section 2: Typography */}
          <section className="settings-section">
            <div className="section-header">
              <Type size={16} className="section-icon" />
              <h3 className="section-title">Typography & Layout</h3>
            </div>

            {/* Font Family */}
            <div className="setting-row">
              <label className="setting-label">Typeface Family</label>
              <div className="button-group">
                {fontFaces.map((f) => (
                  <button
                    key={f.id}
                    className={`btn-choice ${
                      settings.fontFamily === f.id || settings.fontFace === f.id ? 'active' : ''
                    }`}
                    onClick={() =>
                      onUpdateSettings({ fontFamily: f.id, fontFace: f.id })
                    }
                  >
                    <span className="choice-label">{f.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Font Size */}
            <div className="setting-row">
              <label className="setting-label">Font Size</label>
              <div className="button-group">
                {fontSizes.map((s) => (
                  <button
                    key={s.id}
                    className={`btn-choice ${settings.fontSize === s.id ? 'active' : ''}`}
                    onClick={() => onUpdateSettings({ fontSize: s.id })}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Line Spacing */}
            <div className="setting-row">
              <label className="setting-label">Line Spacing</label>
              <div className="button-group">
                {lineSpacings.map((ls) => (
                  <button
                    key={ls.id}
                    className={`btn-choice ${settings.lineSpacing === ls.id ? 'active' : ''}`}
                    onClick={() => onUpdateSettings({ lineSpacing: ls.id })}
                  >
                    {ls.label}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Section 3: Visual Filters & Motion */}
          <section className="settings-section">
            <div className="section-header">
              <Sliders size={16} className="section-icon" />
              <h3 className="section-title">Display & Motion</h3>
            </div>

            <div className="setting-toggle-row">
              <div className="toggle-info">
                <span className="toggle-label">CRT Scanline Mesh Overlay</span>
                <span className="toggle-sub">Subtle cathode-ray phosphor scanlines</span>
              </div>
              <input
                type="checkbox"
                checked={settings.crtScanlines ?? true}
                onChange={(e) => onUpdateSettings({ crtScanlines: e.target.checked })}
                className="toggle-checkbox"
                aria-label="Toggle CRT Scanlines"
              />
            </div>

            <div className="setting-toggle-row">
              <div className="toggle-info">
                <span className="toggle-label">Reduced Motion</span>
                <span className="toggle-sub">Dampen rapid shader distortions and transitions</span>
              </div>
              <input
                type="checkbox"
                checked={settings.reducedMotion ?? settings.motionReduced ?? false}
                onChange={(e) =>
                  onUpdateSettings({
                    reducedMotion: e.target.checked,
                    motionReduced: e.target.checked,
                  })
                }
                className="toggle-checkbox"
                aria-label="Toggle Reduced Motion"
              />
            </div>
          </section>

          {/* Section 4: Danger Zone */}
          <section className="settings-section danger-section">
            <div className="section-header">
              <AlertTriangle size={16} className="danger-icon" />
              <h3 className="section-title danger-title">Danger Zone: Ledger Decouple</h3>
            </div>
            <p className="danger-desc">
              Purging stored state removes all bookmarks, backlog transcripts, and resets the vessel to Page 1.
            </p>
            {confirmReset ? (
              <div className="confirm-reset-box">
                <p className="confirm-warning-text">
                  ⚠️ Confirm timeline decoupling? All stored telemetry will be lost.
                </p>
                <div className="btn-row">
                  <button
                    className="btn-danger-confirm"
                    onClick={() => {
                      onResetAllData();
                      setConfirmReset(false);
                      onClose();
                    }}
                  >
                    Confirm Wipe & Reset
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() => setConfirmReset(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="btn-danger-outline"
                onClick={() => setConfirmReset(true)}
              >
                Purge All Storage & Reset
              </button>
            )}
          </section>
        </div>

        <div className="modal-footer">
          <button className="btn-primary-done" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
